import * as THREE from 'three'
import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js';
import CANNON from 'cannon'
import BrickBreaking from './BrickBreaking.js'


export default class Objects
{
    constructor(_options)
    {
        // Options
        this.time = _options.time
        this.resources = _options.resources
        this.materials = _options.materials
        this.physics = _options.physics
        this.shadows = _options.shadows
        this.sounds = _options.sounds
        this.debug = _options.debug
        this.scene = _options.scene

        // Set up
        this.container = new THREE.Object3D()
        this.container.matrixAutoUpdate = false

        this.items = []
        this.floorShadows = []
        this.breakingBricks = [] // Track breaking animations

        this.setParsers()
        this.setMerge()
    }

    setParsers()
    {
        this.parsers = {}

        this.parsers.items = [
            // Shade - matches mesh names like "shadeWhite", "shadeOrange", "shadeGreen", etc.
            // Example: A mesh named "shadeWhite" will get the white matcap material
            {
                regex: /^shade([a-z]+)_?[0-9]{0,3}?/i,
                apply: (_mesh, _options) =>
                {
                    // Find material by extracting color from mesh name
                    // "shadeWhite" -> "white", "shadeOrange" -> "orange", etc.
                    const match = _mesh.name.match(/^shade([a-z]+)_?[0-9]{0,3}?/i)
                    const materialName = `${match[1].substring(0, 1).toLowerCase()}${match[1].substring(1)}` // PastalCase to camelCase
                    let material = this.materials.shades.items[materialName]

                    // Default
                    if(typeof material === 'undefined')
                    {
                        material = new THREE.MeshNormalMaterial()
                    }

                    // Create clone mesh with new material
                    const mesh = _options.duplicated ? _mesh.clone() : _mesh
                    mesh.material = material

                    if(mesh.children.length)
                    {
                        for(const _child of mesh.children)
                        {
                            if(_child instanceof THREE.Mesh)
                            {
                                _child.material = material
                            }
                        }
                    }

                    return mesh
                }
            },

            // Pure - matches mesh names like "pureWhite", "pureOrange", "pureGreen", etc.
            // Example: A mesh named "pureWhite" will get the white solid color material
            {
                regex: /^pure([a-z]+)_?[0-9]{0,3}?/i,
                apply: (_mesh, _options) =>
                {
                    // Find material by extracting color from mesh name
                    // "pureWhite" -> "white", "pureOrange" -> "orange", etc.
                    const match = _mesh.name.match(/^pure([a-z]+)_?[0-9]{0,3}?/i)
                    const materialName = match[1].toLowerCase()
                    let material = this.materials.pures.items[materialName]

                    // Default
                    if(typeof material === 'undefined')
                    {
                        material = new THREE.MeshNormalMaterial()
                    }

                    // Create clone mesh with new material
                    const mesh = _options.duplicated ? _mesh.clone() : _mesh
                    mesh.material = material

                    return mesh
                }
            },

            // Floor
            {
                regex: /^floor_?[0-9]{0,3}?/i,
                apply: (_mesh, _options) =>
                {
                    // Create floor manually because of missing UV
                    const geometry = new THREE.PlaneGeometry(_mesh.scale.x, _mesh.scale.y, 10, 10)
                    const material = this.materials.items.floorShadow.clone()

                    material.uniforms.tShadow.value = _options.floorShadowTexture
                    material.uniforms.uShadowColor.value = new THREE.Color(this.materials.items.floorShadow.shadowColor)
                    material.uniforms.uAlpha.value = 0

                    const mesh = new THREE.Mesh(geometry, material)
                    mesh.matrixAutoUpdate = false
                    mesh.updateMatrix()

                    this.floorShadows.push(mesh)

                    return mesh
                }
            }
        ]

        // Default
        this.parsers.default = {}
        this.parsers.default.apply = (_mesh) =>
        {
            // Create clone mesh with normal material
            const mesh = _mesh.clone()
            mesh.material = this.materials.shades.items.white

            return mesh
        }
    }

    setMerge()
    {
        this.merge = {}
        this.merge.items = {}

        this.merge.container = new THREE.Object3D()
        this.merge.container.matrixAutoUpdate = false
        this.container.add(this.merge.container)

        this.merge.add = (_name, _mesh) =>
        {
            let mergeItem = this.merge.items[_name]

            // Create merge item if not found
            if(!mergeItem)
            {
                mergeItem = {}

                // Geometry
                mergeItem.geometry = new THREE.BufferGeometry()
                mergeItem.geometriesToMerge = []

                // Material
                mergeItem.material = _mesh.material
                mergeItem.material.side = THREE.DoubleSide

                // Mesh
                mergeItem.mesh = new THREE.Mesh(mergeItem.geometry, mergeItem.material)
                this.merge.container.add(mergeItem.mesh)

                // Save
                this.merge.items[_name] = mergeItem
            }

            // Apply the object transform to the geometry and save it for later merge
            const geometry = _mesh.geometry
            _mesh.updateMatrixWorld() // Maybe not
            geometry.applyMatrix(_mesh.matrixWorld)

            mergeItem.geometriesToMerge.push(geometry)
        }

        this.merge.applyMerge = () =>
        {
            for(const _mergeItemName in this.merge.items)
            {
                const mergeItem = this.merge.items[_mergeItemName]

                mergeItem.geometry = BufferGeometryUtils.mergeGeometries(mergeItem.geometriesToMerge) // Should add original geometry
                mergeItem.mesh.geometry = mergeItem.geometry
            }
        }

        this.merge.update = () =>
        {
            for(const _object of this.items)
            {
                if(_object.shouldMerge)
                {
                    const children = [..._object.container.children]
                    for(const _child of children)
                    {
                        const materialName = _child.material.name
                        if(materialName !== '')
                        {
                            this.merge.add(materialName, _child)

                            // Remove from parent
                            _object.container.remove(_child)
                        }
                    }

                    // If no children, remove

                    _object.shouldMerge = false
                }
            }

            // Apply merge
            this.merge.applyMerge()
        }
    }

    getConvertedMesh(_children, _options = {})
    {
        const container = new THREE.Object3D()
        const center = new THREE.Vector3()

        // Go through each base child
        const baseChildren = [..._children]

        for(const _child of baseChildren)
        {
            // Find center
            if(_child.name.match(/^center_?[0-9]{0,3}?/i))
            {
                center.set(_child.position.x, _child.position.y, _child.position.z)
            }

            if(_child instanceof THREE.Mesh)
            {
                // Strategy:
                // - If preserveMaterials: always keep original materials
                // - Else if preserveUnmatched: keep original only when no parser matches
                // - Else: use parser (or default)

                const hasPreserveAll = !!_options.preserveMaterials

                // Try find a parser for this mesh name
                let parser = this.parsers.items.find((_item) => _child.name.match(_item.regex))

                const shouldPreserveUnmatched = !!_options.preserveUnmatched && typeof parser === 'undefined'

                if(hasPreserveAll || shouldPreserveUnmatched)
                {
                    const mesh = _child.clone()
                    container.add(mesh)
                }
                else
                {
                    // Use found parser or default
                    if(typeof parser === 'undefined')
                    {
                        parser = this.parsers.default
                    }

                    const mesh = parser.apply(_child, _options)
                    container.add(mesh)
                }
            }
        }

        // Recenter
        if(center.length() > 0)
        {
            for(const _child of container.children)
            {
                _child.position.sub(center)
            }

            container.position.add(center)
        }

        if(_options.mass && _options.mass === 0)
        {
            container.matrixAutoUpdate = false
            container.updateMatrix()
        }

        return container
    }

    add(_options)
    {
        const object = {}

        object.merged = false
        object.shouldMerge = _options.mass === 0

        // Offset
        const offset = new THREE.Vector3()
        if(_options.offset)
        {
            offset.copy(_options.offset)
        }

        // Rotation
        const rotation = new THREE.Euler()
        if(_options.rotation)
        {
            rotation.copy(_options.rotation)
        }

        // Sleep
        const sleep = typeof _options.sleep === 'undefined' ? true : _options.sleep

        // Container
        object.container = this.getConvertedMesh(_options.base.children, _options)
        object.container.position.copy(offset)
        object.container.rotation.copy(rotation)
        this.container.add(object.container)

        // Animation support - following Three.js standard pattern
        if(_options.animations && _options.animations.length > 0)
        {
            // Create AnimationMixer with the model
            object.mixer = new THREE.AnimationMixer(object.container)
            
            // Create actions for all animations
            object.actions = []
            for(let i = 0; i < _options.animations.length; i++)
            {
                const clip = _options.animations[i]
                
                // Filter out tracks that target objects ending with "_no"
                const filteredTracks = clip.tracks.filter(track => {
                    // Track names are like "ObjectName.position" or "ObjectName.quaternion"
                    const objectName = track.name.split('.')[0]
                    return !objectName.endsWith('_no')
                })
                
                // Only create action if there are tracks remaining after filtering
                if(filteredTracks.length > 0)
                {
                    // Create new clip with filtered tracks
                    const filteredClip = clip.clone()
                    filteredClip.tracks = filteredTracks
                    
                    const action = object.mixer.clipAction(filteredClip)
                    object.actions.push(action)
                }
            }
            
            // Play ALL animations
            for(let i = 0; i < object.actions.length; i++)
            {
                // Set animation speed (1.0 = normal speed, 0.1 = ten times slower)
                // TO MAKE ANIMATION SLOWER: decrease this value (e.g., 0.05 = 20x slower, 0.01 = 100x slower)
                // TO MAKE ANIMATION FASTER: increase this value (e.g., 0.5 = 2x slower, 2.0 = 2x faster)
                object.actions[i].setEffectiveTimeScale(0.001) // Ten times slower
                
                // Enable looping for animations
                object.actions[i].setLoop(THREE.LoopRepeat, Infinity)
                
                object.actions[i].play()
            }
        }

        // Manual morph target cycling (for key shapes)
        let morphTargetMesh = null
        
        // Find mesh with morph targets
        object.container.traverse((child) => {
            if(child.isMesh && child.morphTargetInfluences && child.morphTargetInfluences.length > 0) {
                morphTargetMesh = child
                // console.log('Found morph target mesh:', child.name, 'with', child.morphTargetInfluences.length, 'targets')
            }
        })
        
        // Set up manual morph target cycling
        if(morphTargetMesh) {
            object.morphTargetMesh = morphTargetMesh
            object.currentMorphTarget = 0
            object.morphTargetCycleTime = 0
            object.morphTargetCycleDuration = 20000 // 20 seconds per morph target (10x slower)
        }


        // Deactivate matrix auto update (but not for animated objects)
        if(_options.mass === 0 && !(_options.animations && _options.animations.length > 0))
        {
            object.container.matrixAutoUpdate = false
            object.container.updateMatrix()

            for(const _child of object.container.children)
            {
                _child.matrixAutoUpdate = false
                _child.updateMatrix()
            }
        }

        // Create physics object
        object.collision = this.physics.addObjectFromThree({
            meshes: [..._options.collision.children],
            offset,
            rotation,
            mass: _options.mass,
            sleep
        })

        for(const _child of object.container.children)
        {
            _child.position.sub(object.collision.center)
        }

        // Sound and brick breaking
        if(_options.soundName)
        {
            object.collision.body.addEventListener('collide', (_event) =>
            {
                const relativeVelocity = _event.contact.getImpactVelocityAlongNormal()
                this.sounds.play(_options.soundName, relativeVelocity)

                // Check if this is a brick (not a letter) and if it collided with the car
                if(_options.soundName === 'brick' && this.isBrickObject(_options))
                {
                    // Check if the other body is the car chassis
                    const otherBody = _event.target === object.collision.body ? _event.body : _event.target
                    if(otherBody === this.physics.car.chassis.body)
                    {
                        // Trigger breaking
                        this.breakBrick(object)
                    }
                }
            })
        }

        // Shadow
        // Add shadow
        if(_options.shadow)
        {
            this.shadows.add(object.container, _options.shadow)
        }

        // Time tick event
        if(_options.mass > 0)
        {
            this.time.on('tick', () =>
            {
                object.container.position.copy(object.collision.body.position)
                object.container.quaternion.copy(object.collision.body.quaternion)
            })
        }


        // Animation update (both regular animations and morph targets)
        this.time.on('tick', () =>
        {
            // Update regular animations
            if(object.mixer)
            {
                object.mixer.update(this.time.delta)
            }
            
            // Update manual morph target cycling
            if(object.morphTargetMesh)
            {
                // Update cycle time
                object.morphTargetCycleTime += this.time.delta * 1000 // Convert to milliseconds
                
                // Check if it's time to switch to next morph target
                if(object.morphTargetCycleTime >= object.morphTargetCycleDuration)
                {
                    // Reset all influences to 0
                    for(let i = 0; i < object.morphTargetMesh.morphTargetInfluences.length; i++)
                    {
                        object.morphTargetMesh.morphTargetInfluences[i] = 0
                    }
                    
                    // Set current target to 1
                    object.morphTargetMesh.morphTargetInfluences[object.currentMorphTarget] = 1
                    
                    // Move to next target
                    object.currentMorphTarget = (object.currentMorphTarget + 1) % object.morphTargetMesh.morphTargetInfluences.length
                    
                    // Reset cycle time
                    object.morphTargetCycleTime = 0
                    
                    // console.log('Switched to morph target:', object.currentMorphTarget)
                    // console.log('Influences:', object.morphTargetMesh.morphTargetInfluences)
                }
            }
        })

        // Save
        this.items.push(object)

        return object
    }

    isBrickObject(_options)
    {
        // Check if this is an actual brick (not a letter)
        // Letters use introABase, introHBase, etc.
        // Bricks use brickBase, brickBase2, brickBase3
        if(_options.base)
        {
            // Check if base is one of the brick resources
            const brickResources = [
                this.resources.items.brickBase,
                this.resources.items.brickBase2,
                this.resources.items.brickBase3
            ]
            
            return brickResources.some(brickResource => brickResource && brickResource.scene === _options.base)
        }
        return false
    }

    breakBrick(brickObject)
    {
        // Check if brick is already breaking
        if(brickObject.isBreaking) return
        brickObject.isBreaking = true

        // Play brick breaking (crate) sound once when the brick actually breaks
        if(this.sounds)
        {
            this.sounds.play('brickBreak', 0)
        }

        // Remove shadow if it exists
        if(this.shadows && this.shadows.items)
        {
            const shadowIndex = this.shadows.items.findIndex(shadow => shadow.reference === brickObject.container)
            if(shadowIndex !== -1)
            {
                const shadow = this.shadows.items[shadowIndex]
                // Remove shadow mesh from scene
                if(shadow.mesh && shadow.mesh.parent)
                {
                    shadow.mesh.parent.remove(shadow.mesh)
                }
                // Dispose shadow material
                if(shadow.material && shadow.material.dispose)
                {
                    shadow.material.dispose()
                }
                // Remove from items array
                this.shadows.items.splice(shadowIndex, 1)
            }
        }

        // Keep original physics body but make it kinematic (provides collision support for stacked bricks)
        // We'll remove it after a short delay to let stacked bricks fall
        if(brickObject.collision && brickObject.collision.body)
        {
            // Make it kinematic so it doesn't move but still provides collision
            brickObject.collision.body.type = CANNON.Body.KINEMATIC
            brickObject.collision.body.velocity.set(0, 0, 0)
            brickObject.collision.body.angularVelocity.set(0, 0, 0)
            
            // Remove it after a short delay to let physics settle
            setTimeout(() => {
                if(brickObject.collision && brickObject.collision.body)
                {
                    this.physics.world.remove(brickObject.collision.body)
                }
            }, 100) // 100ms delay
        }

        // Create breaking (split into 6 faces)
        const breaking = new BrickBreaking({
            time: this.time,
            scene: this.scene || this.container,
            object: brickObject,
            physics: this.physics,
            resources: this.resources,
            debug: this.debug
        })

        // Add breaking to scene
        if(this.scene)
        {
            this.scene.add(breaking.container)
        }
        else
        {
            this.container.add(breaking.container)
        }

        // Track breaking
        this.breakingBricks.push(breaking)

        // Break (split into faces with physics)
        breaking.break()
    }
}
